import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, X } from "lucide-react";
import { getNotifications, readNotifications } from "../lib/api";
import PhoneSetup from "./PhoneSetup";

const KIND_COLOR = { watch: "#00e676", intraday: "#00e5ff", daily: "#a855f7", rescan: "#ffb300", test: "#94a3b8" };

const NotifItem = ({ n, onOpen }) => (
  <button onClick={() => onOpen(n)} data-testid={`notif-${n.id}`}
    className="w-full text-left p-3 rounded-lg border border-[#1e293b] hover:border-[#2d3c59] transition-colors">
    <div className="flex items-center gap-2">
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: n.read ? "#2d3c59" : KIND_COLOR[n.kind] }} />
      <span className="text-sm text-[#f8fafc] font-medium truncate">{n.title}</span>
      <span className="ml-auto font-mono text-[10px] text-[#64748b] shrink-0">
        {new Date(n.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
      </span>
    </div>
    <div className="text-xs text-[#94a3b8] mt-1 whitespace-pre-line line-clamp-4">{n.body}</div>
  </button>
);

export default function NotificationBell({ setActive }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["notifications"], queryFn: getNotifications, refetchInterval: 60000 });
  useEffect(() => {
    if (open && data?.unread) readNotifications().then(() => qc.invalidateQueries({ queryKey: ["notifications"] }));
  }, [open, data?.unread, qc]);
  const toggle = () => setOpen((o) => !o);
  const openItem = (n) => { setActive(n.tab || "recommendations"); setOpen(false); };

  return (
    <>
      <button data-testid="notif-bell" onClick={toggle} aria-label="Notifications"
        className="relative w-8 h-8 rounded-lg border border-[#2d3c59] flex items-center justify-center text-[#94a3b8] hover:text-[#00e5ff] transition-colors">
        <Bell className="w-4 h-4" />
        {data?.unread > 0 && (
          <span data-testid="notif-unread" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#ff3b30] text-white text-[10px] font-mono flex items-center justify-center">
            {data.unread > 99 ? "99+" : data.unread}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 40 }}
            data-testid="notif-panel"
            className="fixed top-0 right-0 h-[100dvh] w-full sm:w-[420px] z-[70] bg-[#0b1220] border-l border-[#1e293b] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-[#1e293b]">
              <div className="font-semibold text-[#f8fafc]">Notifications</div>
              <button data-testid="notif-close" onClick={() => setOpen(false)} className="text-[#94a3b8] hover:text-[#f8fafc]"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <PhoneSetup devices={data?.devices || 0} settings={data?.settings || {}} />
              <div className="space-y-2" data-testid="notif-list">
                {(data?.items || []).map((n) => <NotifItem key={n.id} n={n} onOpen={openItem} />)}
                {!data?.items?.length && <div className="text-xs text-[#64748b] text-center py-6" data-testid="notif-empty">No alerts yet. Add picks to your watchlist to get buy/sell alerts.</div>}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
